/* eslint-disable no-param-reassign */

import _ from 'es-toolkit/compat';
import * as yup from 'yup';

import { CORPORATE_STRUCTURE, INDUSTRIES } from '@clearverify/constants';
import { validateSyncWithSchema } from '@clearverify/utils';

import {
  emailValidationSchema,
  feinValidationSchema,
  numberOfEmployeesSimpleValidationSchema,
  phoneNumberValidationSchema,
  taxYearEndDateValidationSchema,
  zipCodeValidationSchema,
} from 'src/constants/validation';
import { collectPropertyValuesCount } from 'src/utils/bulkUpload';

import { TemplateField } from '../constants';
import type { ExtendedInvitedEmployerData, InvitedEmployerData } from '../types';

const NOT_UNIQUE_MESSAGE = ' - Not unique value';
const NOT_VALID_MESSAGE = ' - Invalid value';
const REQUIRED_MESSAGE = 'Required value';

export const validateEmployers = (
  employers: InvitedEmployerData[],
): ExtendedInvitedEmployerData[] => {
  const inviteEmails = collectPropertyValuesCount(
    employers,
    TemplateField.emailForInvitation,
    value => (_.isString(value) ? value.trim() : value),
  );
  const resultData: ExtendedInvitedEmployerData[] = [];
  for (const employer of employers) {
    const isEmailForInvitationUnique = employer.emailForInvitation
      ? (inviteEmails[(employer.emailForInvitation || '_').trim()] || 1) === 1
      : true;

    let valid = isEmailForInvitationUnique;
    if (!isEmailForInvitationUnique) employer.emailForInvitation += NOT_UNIQUE_MESSAGE;

    const validationErrors = validateSyncWithSchema(invitedEmployerValidationSchema, employer);
    if (!_.isEmpty(validationErrors)) valid = false;

    for (const property in validationErrors) {
      if (
        [TemplateField.corporateStructure, TemplateField.industry].includes(
          property as TemplateField,
        )
      ) {
        const peoProp = _.get(employer, property);
        _.set(employer, property, peoProp + NOT_VALID_MESSAGE);
      } else {
        // eslint-disable-next-line no-lonely-if
        if (property in employer) {
          const peoProp = _.get(employer, property);
          _.set(employer, property, `${peoProp} - ${validationErrors[property]}`);
        } else {
          _.set(employer, property, validationErrors[property]);
        }
      }
    }

    const result: ExtendedInvitedEmployerData = {
      ...employer,
      id: window.crypto.randomUUID(),
      valid,
    };
    resultData.push(result);
  }

  return resultData;
};

const stringValidationSchema = yup.string().trim();

const invitedEmployerValidationSchema: yup.ObjectSchema<InvitedEmployerData> = yup.object({
  [TemplateField.feinCommercialName]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.contactEmail]: emailValidationSchema.trim().required(REQUIRED_MESSAGE),
  [TemplateField.contactPhone]: phoneNumberValidationSchema.nullable(),
  [TemplateField.corporateStructure]: stringValidationSchema.oneOf(CORPORATE_STRUCTURE).nullable(),
  [TemplateField.displayName]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.emailForInvitation]: emailValidationSchema.trim().nullable(),
  [TemplateField.fein]: feinValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqAddress]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqCity]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqCounty]: stringValidationSchema.trim().nullable(),
  [TemplateField.hqState]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqZipCode]: zipCodeValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.industry]: stringValidationSchema.oneOf(INDUSTRIES).nullable(),
  [TemplateField.numberOfEmployees]: numberOfEmployeesSimpleValidationSchema,
  [TemplateField.feinRegisteredBusinessName]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.taxYearEndDate]: taxYearEndDateValidationSchema.nullable(),
});
